import React, { createContext, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSmartlook } from "@services/axios/safeSmartlook";
import { LOCAL_STORAGE } from "@constants";
import {
  UseRefreshTokenResult,
  parseTokenExpireDate,
  useRefreshToken,
} from "@hooks/useRefreshToken";
import useMessage from "@hooks/useMessage";
import { PRODUCT_TYPE } from "@utils/enums";
import { AmplifyAuth } from "@classes/infra/auth/AmplifyAuth";
import {
  AmplifyAuthResponse,
  AmplifyError,
} from "src/entities/AmplifyAuthResponse";
import { featureFlags } from "@settings";
import { ProductPermission } from "@hooks/api/v2/products";

export type User = {
  accessToken?: string | null;
  refreshToken?: string | null;
  tokenHasDataOfCorbanId?: number | null;
  name?: string | null;
  uniqueId?: string | null;
  identifier?: string | null;
  enableFaceMatch?: boolean | null;
  isChecked?: boolean | null;
  checkUrl?: string | null;
  supportChat?: boolean | null;
  allowIlliterate?: boolean | null;
  allowLegalRepresentative?: boolean | null;
  allowCardLegalRepresentative?: boolean | null;
  corbanId?: number | null;
  corbanName?: string | null;
  products?: Partial<Record<PRODUCT_TYPE, string>>;
  corbanProducts?: Partial<Record<PRODUCT_TYPE, string>>;
  id?: number | null;
  username?: string;
  ip?: string | null;
  productsV2?: ProductPermission[];
  groups?: unknown[];
};

type ProductTypeList = Array<{ nome: string; tipo_produto: PRODUCT_TYPE }>;

export type AuthContext = {
  user: User | null;
  accessToken: string;
  name: string;
  identifier: string;
  loading: boolean;
  permissions: {
    allowIlliterate: boolean;
    allowLegalRepresentative: boolean;
    allowCardLegalRepresentative: boolean;
  };
  updateUser: (user?: Partial<User>) => void;
  clearUser: () => void;
  setProducts: (productTypeList: ProductTypeList) => void;
  setCorbanProducts: (productTypeList: ProductTypeList) => void;
  hasProduct: (productType: PRODUCT_TYPE) => boolean;
  hasProductV2: (productType: PRODUCT_TYPE) => boolean;
  loginMfa: (cpf: string, password: string) => Promise<AmplifyAuthResponse>;
  confirmLoginMfa: (
    code: string,
    signInStep: string,
  ) => Promise<AmplifyAuthResponse>;
  logoutMfa: () => Promise<void>;
  onLogout: (redirectTo?: string) => void;
  onExpireSession: (msg?: string | null) => void;
  refreshAccessToken: UseRefreshTokenResult["refreshAccessToken"];
  ensureAccessToken: UseRefreshTokenResult["ensureAccessToken"];
};

export const AuthContext = createContext<AuthContext>({
  user: null,
  accessToken: "",
  name: "",
  identifier: "",
  loading: true,
  permissions: {
    allowIlliterate: false,
    allowLegalRepresentative: false,
    allowCardLegalRepresentative: false,
  },
  updateUser: () => {},
  clearUser: () => {},
  setProducts: () => {},
  setCorbanProducts: () => {},
  hasProduct: () => false,
  hasProductV2: () => false,
  loginMfa: () => Promise.resolve() as never,
  confirmLoginMfa: () => Promise.resolve() as never,
  logoutMfa: () => Promise.resolve(),
  onLogout: () => {},
  onExpireSession: () => {},
  refreshAccessToken: () => Promise.resolve(false),
  ensureAccessToken: () => Promise.resolve(false),
});

export const AuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const message = useMessage();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [loggedInCognito, setLoggedInCognito] = useState(false);

  const accessToken = user?.accessToken || "";
  const refreshToken = user?.refreshToken || "";

  const isLoginMfa = featureFlags.enableLoginMfaCef;

  const authService = React.useMemo(() => new AmplifyAuth(), []);

  const loginMfa = useCallback(
    async (username: string, password: string) => {
      localStorage.clear();
      localStorage.setItem("username", username);
      return await authService.login(username, password);
    },
    [authService],
  );

  const updateUser = useCallback((userData?: Partial<User>) => {
    setUser((curUser) => {
      if (curUser || userData) {
        const newUser: User = {
          ...(curUser || {}),
          ...(userData || {}),
        };

        void smartlookIdentify(newUser);

        const userDataStr = JSON.stringify(newUser);
        localStorage.setItem(LOCAL_STORAGE.USER, userDataStr);

        return newUser;
      } else {
        localStorage.removeItem(LOCAL_STORAGE.USER);
        void smartlookAnonymize();
        return null;
      }
    });
  }, []);

  const confirmLoginMfa = useCallback(
    async (
      challengeResponse: string,
      signInStep: string,
    ): Promise<AmplifyAuthResponse> => {
      try {
        const response = await authService.confirmLogin(
          challengeResponse,
          signInStep,
        );
        setLoggedInCognito(true);
        const access = localStorage.getItem(LOCAL_STORAGE.COGNITO_ACCESS_TOKEN);
        const refresh = localStorage.getItem(
          LOCAL_STORAGE.COGNITO_REFRESH_TOKEN,
        );
        updateUser({
          accessToken: access,
          refreshToken: refresh,
        });
        return response;
      } catch (error: unknown) {
        const amplifyError = error as AmplifyError;
        if (
          amplifyError.name === "NotAuthorizedException" &&
          amplifyError.message.includes("expired")
        ) {
          const expiredError = "Código expirado. Faça o login novamente.";
          throw new Error(expiredError);
        }
        throw new Error("Erro na confirmação do login.");
      } finally {
        setLoading(false);
      }
    },
    [authService, updateUser],
  );

  const logoutMfa = useCallback(async () => {
    try {
      await authService.signOut();
      localStorage.clear();
    } catch (error) {
      console.error("Erro ao sair do Cognito", error);
    }
  }, [authService]);

  const clearUser = useCallback(() => {
    setUser(null);
    localStorage.removeItem(LOCAL_STORAGE.USER);
    void smartlookAnonymize();
  }, []);

  const productTypeListToMap = useCallback(
    (productTypeList: ProductTypeList) => {
      return productTypeList.reduce<NonNullable<User["products"]>>(
        (acc, product) => {
          acc[product.tipo_produto] = product.nome;
          return acc;
        },
        {},
      );
    },
    [],
  );

  const setProducts: AuthContext["setProducts"] = useCallback(
    (productTypeList) => {
      setUser((curUser) => ({
        ...curUser,
        products: productTypeListToMap(productTypeList),
      }));
    },
    [productTypeListToMap],
  );

  const setCorbanProducts: AuthContext["setCorbanProducts"] = useCallback(
    (productTypeList) => {
      setUser((curUser) => ({
        ...curUser,
        corbanProducts: productTypeListToMap(productTypeList),
      }));
    },
    [productTypeListToMap],
  );

  const hasProduct = useCallback(
    (productType: PRODUCT_TYPE) => !!user?.products?.[productType],
    [user?.products],
  );

  const hasProductV2 = useCallback(
    (productType: PRODUCT_TYPE) =>
      !!user?.productsV2?.some((p) => p.legacy_id === productType),
    [user?.productsV2],
  );

  const onUpdateAccessToken = useCallback(
    (accessToken: string) => updateUser({ accessToken }),
    [updateUser],
  );

  const onLogout = useCallback(
    (redirectTo?: string) => {
      if (isLoginMfa && loggedInCognito) {
        void logoutMfa();
      }
      sessionStorage.clear();
      clearUser();

      if (redirectTo && typeof redirectTo === "string") {
        location.href = redirectTo;
      } else if (location.pathname !== "/login") {
        void navigate("/login");
      }
    },
    [clearUser, navigate, logoutMfa, isLoginMfa, loggedInCognito],
  );

  const onExpireSession = useCallback(
    (msg?: string | null) => {
      onLogout();
      if (typeof msg === "string") {
        if (msg) void message.error(msg);
      } else {
        void message.error("Sua sessão expirou, faça o login novamente.");
      }
    },
    [onLogout, message],
  );

  const { refreshAccessToken, ensureAccessToken } = useRefreshToken({
    accessToken,
    refreshToken,
    onUpdateAccessToken,
    onExpireSession,
  });

  const smartlookIdentify = async (user: User) => {
    const Smartlook = await getSmartlook();

    if (Smartlook.initialized()) {
      if (user.identifier) {
        Smartlook.identify(user.identifier, {
          name: user.name || "",
        });
      }
    }
  };

  const smartlookAnonymize = async () => {
    const Smartlook = await getSmartlook();

    if (Smartlook.initialized()) {
      Smartlook.anonymize();
    }
  };

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== LOCAL_STORAGE.USER) return;

      if (e.oldValue && !e.newValue) onLogout();
      if (e.newValue && e.oldValue !== e.newValue) {
        updateUser(JSON.parse(e.newValue) as User);
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [onLogout, updateUser]);

  useEffect(() => {
    const userData = localStorage.getItem(LOCAL_STORAGE.USER);

    if (userData) {
      const user = JSON.parse(userData) as User;

      const isCognito = !!localStorage.getItem(
        LOCAL_STORAGE.COGNITO_REFRESH_TOKEN,
      );

      if (!isCognito) {
        const isRefreshTokenExpired =
          parseTokenExpireDate(user.refreshToken) - Date.now() < 0;
        const isAccessTokenExpired =
          parseTokenExpireDate(user.accessToken) - Date.now() < 0;

        if (isRefreshTokenExpired || isAccessTokenExpired) {
          onExpireSession();
          setLoading(false);
          return;
        }
      }

      setUser(user);
      void smartlookIdentify(user);
    }

    setLoading(false);
  }, [onExpireSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        name: user?.name || "",
        identifier: user?.identifier || "",
        loading,
        permissions: {
          allowIlliterate: user?.allowIlliterate || false,
          allowLegalRepresentative: user?.allowLegalRepresentative || false,
          allowCardLegalRepresentative:
            user?.allowCardLegalRepresentative || false,
        },
        updateUser,
        clearUser,
        setProducts,
        setCorbanProducts,
        hasProduct,
        hasProductV2,
        loginMfa,
        confirmLoginMfa,
        logoutMfa,
        onLogout,
        onExpireSession,
        refreshAccessToken,
        ensureAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
